import { Info, OpenInNew } from '@mui/icons-material';
import {
    Box,
    TextField,
    Typography,
    styled,
    Button,
    Tooltip,
    Collapse,
    Chip,
    Skeleton,
    IconButton,
} from '@mui/material';
import { ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { TemplateDiscoveryService } from 'src/services/TemplateDiscoveryService';
import { CopyButton } from 'src/components/common/CopyButton';
import { getDesignTypeIcon, getDesignTypeColor } from 'src/utils/designTypeUtils';
import { DesignCreationType } from 'src/services/DesignConductorService';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { DocumentSourceQuery } from 'src/hooks/useDocumentSource';

type DocumentSourceInputsProps = {
    documentUri: string;
    pdsResult: string;
    showPdsResult: boolean;
    dpcId: string;
    showDpcId: boolean;
    documentSourceQuery: DocumentSourceQuery;
    onDocumentUriChange?: (uri: string) => void;
    onPdsResultChange?: (uri: string) => void;
    onShowPdsResultChange?: (show: boolean) => void;
    onDpcIdChange?: (id: string) => void;
    onShowDpcIdChange?: (show: boolean) => void;
};

const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
}));

const SiteResultTooltipText = 'Add a Site Result (PDS Result) URI to see the preview in the right column for comparison purposes. It will not affect the matching process.';

const getDesignCreationType = (documentSource: any): DesignCreationType | null => {
    const firstPanel = documentSource?.metadata?.documentSources?.panels?.[0];

    if (!firstPanel) return null;

    if (firstPanel.source === 'TEMPLATE_TOKEN') {
        return 'Static';
    }
    if (firstPanel.source === 'DYNAMIC_DESIGN') {
        return 'Dynamic';
    }
    return null;
};

const getNonStaticDesignConcept = (documentSource: any): string | null => {
    const firstPanel = documentSource?.metadata?.documentSources?.panels?.[0];

    if (!firstPanel) return null;

    if ((firstPanel.source === 'DYNAMIC_DESIGN' || firstPanel.source === 'AUTOLAYOUT_DESIGN') && typeof firstPanel.data === 'object') {
        return `${firstPanel.data.designConceptId}`;
    }

    return null;
};

const getTemplateToken = (documentSource: any): string | null => {
    const firstPanel = documentSource?.metadata?.documentSources?.panels?.[0];

    if (!firstPanel) return null;

    if (firstPanel.source === 'TEMPLATE_TOKEN') {
        return firstPanel.data as string;
    }

    return null;
};

export const DocumentSourceInputs = ({
    documentUri,
    pdsResult,
    showPdsResult,
    dpcId,
    showDpcId,
    documentSourceQuery,
    onDocumentUriChange,
    onPdsResultChange,
    onShowPdsResultChange,
    onDpcIdChange,
    onShowDpcIdChange,
}: DocumentSourceInputsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    // Use the passed-in document source result instead of querying it ourselves
    const { data: documentSource, isLoading: isLoadingDocumentSource } = documentSourceQuery;

    const designType = getDesignCreationType(documentSource);
    const isStaticDesign = designType === 'Static';
    const templateToken = getTemplateToken(documentSource);
    const templateIdentifier = documentSource?.templateIdentifier?.value;

    // Query template discovery for static designs to get the design concept ID
    const { data: staticDesignConceptId, isLoading: isLoadingTemplateDiscovery } = useQuery({
        queryKey: ['templateDiscovery', templateToken, accessToken],
        queryFn: async () => {
            if (!templateToken || !accessToken) {
                throw new Error('Missing template token or access token');
            }

            const ensembleLines = await TemplateDiscoveryService.getEnsembleLinesFromTemplateToken(
                templateToken,
                accessToken,
            );

            return ensembleLines[0]?.ensembles[0]?.designProperties?.isProminentColorVariant !== undefined
                ? ensembleLines[0].designConceptId
                : null;
        },
        enabled: isStaticDesign && !!templateToken && !!accessToken && !!documentSource,
        retry: false,
        refetchOnWindowFocus: false,
        suspense: false,
    });

    const designConcept = isStaticDesign
        ? staticDesignConceptId
        : getNonStaticDesignConcept(documentSource);

    const isLoading = isLoadingDocumentSource || (isStaticDesign && isLoadingTemplateDiscovery);

    return (
        <Container>
            <Box>
                <Typography gutterBottom fontWeight="medium" variant="subtitle1">
                    Document Source
                </Typography>
                <TextField
                    fullWidth
                    label="Document Source URI"
                    placeholder="https://storage.documents.cimpress.io/..."
                    size="small"
                    value={documentUri}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onDocumentUriChange?.(e.target.value)}
                />
                {documentUri && isLoading && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" variant="body2">
                                Design Creation Type:
                            </Typography>
                            <Skeleton animation="wave" height={32} width={120} />
                        </Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                Template Identifier:
                            </Typography>
                            <Skeleton animation="wave" height={24} width={200} />
                        </Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                        }}
                        >
                            <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                Design Concept:
                            </Typography>
                            <Skeleton animation="wave" height={24} width={80} />
                        </Box>
                    </Box>
                )}
                {documentUri && !isLoading && designType && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" variant="body2">
                                Design Creation Type:
                            </Typography>
                            <Chip
                                color={getDesignTypeColor(designType)}
                                icon={getDesignTypeIcon(designType)}
                                label={`${designType} Design`}
                                size="small"
                            />
                        </Box>
                        {templateIdentifier && (
                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                            }}
                            >
                                <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                    Template Identifier:
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    minWidth: 0,
                                    flex: 1,
                                    marginTop: '1px',
                                    gap: 2,
                                }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            wordBreak: 'break-all',
                                            minWidth: 0,
                                        }}
                                        variant="body2"
                                    >
                                        {templateIdentifier}
                                    </Typography>
                                    <CopyButton
                                        size="small"
                                        textToCopy={templateIdentifier}
                                        tooltipTitle="Copy template identifier"
                                    />
                                </Box>
                            </Box>
                        )}
                        {designConcept && (
                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                            }}
                            >
                                <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                    Design Concept:
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    minWidth: 0,
                                    flex: 1,
                                    marginTop: '1px',
                                    gap: 2,
                                }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            wordBreak: 'break-all',
                                            minWidth: 0,
                                        }}
                                        variant="body2"
                                    >
                                        {designConcept}
                                    </Typography>
                                    <CopyButton
                                        size="small"
                                        textToCopy={designConcept}
                                        tooltipTitle="Copy design concept"
                                    />
                                    <Tooltip title="View design concept in Tagging Tool">
                                        <IconButton
                                            sx={{
                                                padding: 0.5,
                                                color: 'text.secondary',
                                                width: 16,
                                                height: 16,
                                                '& .MuiSvgIcon-fontSizeSmall': {
                                                    fontSize: '0.875rem',
                                                },
                                            }}
                                            onClick={(): void => window.open(`https://tagging.gallery.vpsvc.com/?designConceptId=${designConcept}`, '_blank')?.focus()}
                                        >
                                            <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <Box>
                {!showPdsResult && (
                <Tooltip title={SiteResultTooltipText}>
                    <Button
                        color="secondary"
                        size="small"
                        variant="text"
                        onClick={(): void => onShowPdsResultChange?.(true)}
                    >
                        + Add Site Result
                    </Button>
                </Tooltip>
                )}
                <Collapse in={showPdsResult}>
                    <Box>
                        <Typography gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }} variant="subtitle1">
                            Site Result
                            <Tooltip title={SiteResultTooltipText}>
                                <Info sx={{
                                    ml: 1, height: 16, width: 16, color: 'primary.main',
                                }}
                                />
                            </Tooltip>
                        </Typography>
                        <TextField
                            fullWidth
                            label="Site Result URI"
                            placeholder="https://..."
                            size="small"
                            value={pdsResult}
                            onChange={(e: ChangeEvent<HTMLInputElement>): void => onPdsResultChange?.(e.target.value)}
                        />
                    </Box>
                </Collapse>
            </Box>
            <Box>
                {!showDpcId && (
                <Tooltip title="Add a Design Personalization Context ID to skip the DPCM call and use this ID directly in matching requests.">
                    <Button
                        color="secondary"
                        size="small"
                        variant="text"
                        onClick={(): void => onShowDpcIdChange?.(true)}
                    >
                        + Add DPC ID
                    </Button>
                </Tooltip>
                )}
                <Collapse in={showDpcId}>
                    <Box>
                        <Typography gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }} variant="subtitle1">
                            Design Personalization Context ID
                            <Tooltip title="Add a Design Personalization Context ID to skip the DPCM call and use this ID directly in matching requests.">
                                <Info sx={{
                                    ml: 1, height: 16, width: 16, color: 'primary.main',
                                }}
                                />
                            </Tooltip>
                        </Typography>
                        <TextField
                            fullWidth
                            label="DPC ID"
                            placeholder="00000000-0000-0000-0000-000000000000"
                            size="small"
                            value={dpcId}
                            onChange={(e: ChangeEvent<HTMLInputElement>): void => onDpcIdChange?.(e.target.value)}
                        />
                    </Box>
                </Collapse>
            </Box>
        </Container>
    );
};
